/**
 * Natural-language quick add (Google "Lunch Fri 1pm", Fastmail), English and German: finds a day, a time or a time
 * range in the text and returns the title without them. Deliberately small and predictable: what it does not
 * recognise stays in the title.
 *
 *   "Lunch fri 1pm"                    → Friday 13:00, default length
 *   "Team call tomorrow 14:00-15:30"   → tomorrow 14:00–15:30
 *   "Zahnarzt am 12.11. um 10 Uhr"     → 12 Nov 10:00
 *   "Workshop next monday"             → next Monday, all day
 */

export interface QuickParse {
  title: string;
  start?: Date;
  end?: Date;
  allDay?: boolean;
}

const DAYS: Record<string, number> = {
  sun: 0, sunday: 0, so: 0, sonntag: 0,
  mon: 1, monday: 1, mo: 1, montag: 1,
  tue: 2, tues: 2, tuesday: 2, di: 2, dienstag: 2,
  wed: 3, wednesday: 3, mi: 3, mittwoch: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4, do: 4, donnerstag: 4,
  fri: 5, friday: 5, fr: 5, freitag: 5,
  sat: 6, saturday: 6, sa: 6, samstag: 6,
};
const MONTHS: Record<string, number> = {
  jan: 0, january: 0, januar: 0, feb: 1, february: 1, februar: 1, mar: 2, march: 2, mär: 2, märz: 2, maerz: 2, apr: 3, april: 3,
  may: 4, mai: 4, jun: 5, june: 5, juni: 5, jul: 6, july: 6, juli: 6, aug: 7, august: 7, sep: 8, sept: 8, september: 8,
  oct: 9, october: 9, okt: 9, oktober: 9, nov: 10, november: 10, dec: 11, december: 11, dez: 11, dezember: 11,
};

const atMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const plusDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** "13", "1pm", "13:30", "9.30", "10 uhr", "1:15 pm" → minutes since midnight. */
function timeOf(hour: string, minute: string | undefined, suffix: string | undefined): number | undefined {
  let h = Number(hour);
  const m = minute ? Number(minute) : 0;
  const s = suffix?.toLowerCase().replace(/\./g, '');
  if (s === 'pm' && h < 12) h += 12;
  if (s === 'am' && h === 12) h = 0;
  return h <= 24 && m < 60 ? h * 60 + m : undefined;
}

export function parseQuickAdd(text: string, now = new Date(), defaultMinutes = 30): QuickParse {
  let rest = ` ${text} `;
  let day: Date | undefined;
  let from: number | undefined;
  let to: number | undefined;
  const cut = (match: RegExpExecArray) => {
    rest = rest.slice(0, match.index) + ' ' + rest.slice(match.index + match[0].length);
  };

  // Time range first ("14:00-15:30", "von 9 bis 10 Uhr", "1-2pm"), then a single time ("at 1pm", "um 10 Uhr").
  const T = String.raw`(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?`;
  const range = new RegExp(String.raw`\s(?:from\s|von\s)?${T}\s*(?:-|–|to|bis)\s*${T}(?:\s*uhr)?(?=\s)`, 'i').exec(rest);
  if (range) {
    const suffix = range[6] ?? range[3];
    from = timeOf(range[1] ?? '', range[2], range[3] ?? (range[6] && Number(range[1]) <= Number(range[4]) ? range[6] : undefined));
    to = timeOf(range[4] ?? '', range[5], suffix);
    if (from !== undefined && to !== undefined) cut(range);
    else from = to = undefined;
  }
  if (from === undefined) {
    const single = new RegExp(String.raw`\s(?:at\s|um\s|@\s?)?(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.|uhr)(?=\s)|\s(?:at|um|@)\s?(\d{1,2})(?:[:.](\d{2}))?(?=\s)|\s(\d{1,2}):(\d{2})(?=\s)`, 'i').exec(rest);
    if (single) {
      from = single[1] !== undefined ? timeOf(single[1], single[2], single[3]?.toLowerCase() === 'uhr' ? undefined : single[3]) : single[4] !== undefined ? timeOf(single[4], single[5], undefined) : timeOf(single[6] ?? '', single[7], undefined);
      if (from !== undefined) cut(single);
    }
  }

  // Day: today/tomorrow, (next) weekday, "12.11.", "11/12", "12 nov", "nov 12".
  const today = atMidnight(now);
  const rel = /\s(today|heute|tomorrow|morgen|übermorgen|uebermorgen|day after tomorrow)(?=\s)/i.exec(rest);
  if (rel) {
    const word = (rel[1] ?? '').toLowerCase();
    day = word === 'today' || word === 'heute' ? today : word === 'tomorrow' || word === 'morgen' ? plusDays(today, 1) : plusDays(today, 2);
    cut(rel);
  }
  if (!day) {
    const weekday = new RegExp(String.raw`\s(?:(next|nächsten|nächster|naechsten|kommenden)\s)?(?:on\s|am\s)?(${Object.keys(DAYS).join('|')})\.?(?=\s)`, 'i').exec(rest);
    // Two-letter German abbreviations only with "am"/"next" (they are common words: "so", "do", "mi").
    const word = weekday?.[2]?.toLowerCase() ?? '';
    if (weekday && (word.length > 2 || /\s(am|next|nächsten)\s/i.test(weekday[0]))) {
      const target = DAYS[word] ?? 0;
      let diff = (target - today.getDay() + 7) % 7;
      if (diff === 0 || weekday[1]) diff = diff === 0 ? 7 : diff;
      day = plusDays(today, diff);
      cut(weekday);
    }
  }
  if (!day) {
    const numeric = /\s(?:am\s|on\s)?(\d{1,2})\.(\d{1,2})\.(\d{2,4})?(?=\s)|\s(?:on\s)?(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?(?=\s)/i.exec(rest);
    const named = new RegExp(String.raw`\s(?:am\s|on\s)?(\d{1,2})\.?\s(${Object.keys(MONTHS).join('|')})\.?(?=\s)|\s(${Object.keys(MONTHS).join('|')})\.?\s(\d{1,2})(?:st|nd|rd|th)?(?=\s)`, 'i').exec(rest);
    let d: number | undefined;
    let m: number | undefined;
    let y: number | undefined;
    if (numeric) {
      // German "12.11." is day.month; "11/12" is US month/day.
      [d, m, y] = numeric[1] ? [Number(numeric[1]), Number(numeric[2]) - 1, numeric[3] ? Number(numeric[3]) : undefined] : [Number(numeric[5]), Number(numeric[4]) - 1, numeric[6] ? Number(numeric[6]) : undefined];
      cut(numeric);
    } else if (named) {
      [d, m] = named[1] ? [Number(named[1]), MONTHS[(named[2] ?? '').toLowerCase()]] : [Number(named[4]), MONTHS[(named[3] ?? '').toLowerCase()]];
      cut(named);
    }
    if (d !== undefined && m !== undefined && d >= 1 && d <= 31 && m >= 0 && m <= 11) {
      const year = y === undefined ? today.getFullYear() : y < 100 ? 2000 + y : y;
      day = new Date(year, m, d);
      // A date earlier this year without a year means next year.
      if (y === undefined && day < today) day = new Date(year + 1, m, d);
    }
  }

  const title = rest.replace(/\s+(at|um|on|am)\s*$/i, '').replace(/\s+/g, ' ').trim();
  if (!day && from === undefined) return { title: text.trim() };
  if (from === undefined) {
    const start = day ?? today;
    return { title, start, end: plusDays(start, 1), allDay: true };
  }
  let base = day ?? today;
  // A time already past today without a day means tomorrow.
  if (!day && base.getTime() + from * 60_000 < now.getTime()) base = plusDays(base, 1);
  const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), Math.floor(from / 60), from % 60);
  const endMinutes = to !== undefined && to > from ? to : from + defaultMinutes;
  const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), Math.floor(endMinutes / 60), endMinutes % 60);
  return { title, start, end, allDay: false };
}
